import { computed, h, onMounted, ref } from 'vue';
import { ElButton, ElMessage, ElMessageBox } from 'element-plus';

const emptyInfo = () => ({
  loaded: false,
  abiVersion: 0,
  version: '',
  currentPath: 'C:\\mazda\\plugin\\active.json',
  loadedPath: '',
  lastError: '',
  lifecycleState: 'unloaded',
  activeArtifact: '',
  activeSha256: '',
  candidateArtifact: '',
  candidateSha256: '',
  candidateTransaction: '',
  canaryPercent: 0,
  candidateCalls: 0,
  candidateFailures: 0,
  candidateTimeouts: 0,
  zombieCount: 0,
  history: [],
});

export function useMazdaPlugin(mazdaPost, getServerName = () => '') {
  const loading = ref(false);
  const uploading = ref(false);
  const promoting = ref(false);
  const aborting = ref(false);
  const reloading = ref(false);
  const rollingBack = ref(false);
  const selectedFile = ref(null);
  const info = ref(emptyInfo());
  const operationNotice = ref(null);
  // "不同步对端"开关：打开后上传/提升/撤销/回滚/重载仅处理本端服务器
  const localOnly = ref(false);
  let infoRequestId = 0;

  const operationBusy = computed(() =>
    uploading.value || promoting.value || aborting.value ||
    reloading.value || rollingBack.value,
  );
  const coordinatorAllowed = computed(() => getServerName() !== 'US Server');

  const historyRows = computed(() =>
    (info.value.history || []).map((name) => ({
      name,
      active: name === info.value.activeArtifact,
      candidate: name === info.value.candidateArtifact,
    })),
  );
  const hasCandidate = computed(() =>
    !!info.value.candidateTransaction && info.value.lifecycleState === 'canary',
  );

  const applyInfo = (data = {}) => {
    info.value = {
      ...emptyInfo(),
      ...data,
      loaded: !!data.loaded,
      abiVersion: Number(data.abiVersion || 0),
      canaryPercent: Number(data.canaryPercent || 0),
      candidateCalls: Number(data.candidateCalls || 0),
      candidateFailures: Number(data.candidateFailures || 0),
      candidateTimeouts: Number(data.candidateTimeouts || 0),
      zombieCount: Number(data.zombieCount || 0),
      history: Array.isArray(data.history) ? data.history : [],
    };
  };

  const errorMessage = (fallback, error) =>
    error?.response?.data?.message || error?.message || fallback;
  const deploymentTarget = () => localOnly.value
    ? '本机（不同步对端）'
    : (getServerName() === 'Local Server' ? '本机' : 'CN/US 两端');

  const clearOperationNotice = () => {
    operationNotice.value = null;
  };

  // 统一的 DLL 操作确认弹窗：长制品名/哈希用可换行、可复制的等宽块展示，
  // 避免超长无空格文件名撑破弹窗导致显示不全。
  const artifactBlock = (text, { copyable = true } = {}) => {
    const copy = () => {
      navigator.clipboard?.writeText(text).then(
        () => ElMessage.success('已复制'),
        () => ElMessage.warning('复制失败，请手动选择复制'),
      );
    };
    const lines = String(text || '').split('\n');
    return h('div', { class: 'dll-confirm-artifact' }, [
      h('div', { class: 'dll-confirm-artifact-text' },
        lines.map((line) => h('div', { class: 'dll-confirm-line' }, line))),
      copyable
        ? h(ElButton, {
            class: 'dll-confirm-copy',
            size: 'small',
            text: true,
            type: 'primary',
            onClick: copy,
          }, () => '复制')
        : null,
    ]);
  };

  const confirmDialog = (title, contentVNode) => ElMessageBox({
    title,
    message: () => h('div', { class: 'dll-confirm-body' }, [contentVNode]),
    showCancelButton: true,
    confirmButtonText: '确认',
    cancelButtonText: '取消',
    type: 'warning',
    autofocus: false,
    closeOnClickModal: false,
    customStyle: { maxWidth: '560px', width: 'calc(100vw - 48px)' },
  });

  const operationFailure = (fallback, source) => {
    const data = source?.response?.data || source || {};
    const details = data?.details || {};
    const parts = [data?.message || source?.message || fallback];
    const peerCommitted = details.peerCommitSucceeded === true ||
      details?.peerCommit?.body?.success === true;
    const localCommitFailed = details.localCommitSucceeded === false ||
      !!details.localCommitError;

    if (peerCommitted && localCommitFailed) {
      parts.push('事务状态：对端提交成功，本机提交失败');
    } else if (data.peerApplied === true && data.localApplied === false) {
      parts.push('同步状态：对端执行成功，本机执行失败');
    }

    if (details.compensationSucceeded === true || details.compensated === true) {
      parts.push('补偿结果：对端已回滚');
    } else if (details.compensationSucceeded === false || details.compensated === false) {
      parts.push('补偿结果：未完全确认，必须人工核对两端活动 SHA-256');
    }
    if (details.localCommitError) {
      parts.push(`本机错误：${details.localCommitError}`);
    }
    if (data.syncWarning && !parts.includes(data.syncWarning)) {
      parts.push(data.syncWarning);
    }

    const requiresManualCheck = data.requiresManualCheck === true ||
      details.requiresManualCheck === true ||
      data.peerApplied === true && data.localApplied === false;
    const message = parts.filter(Boolean).join('；');
    operationNotice.value = {
      title: requiresManualCheck ? '双服务器状态需要人工核对' : 'DLL 操作未完成',
      message,
      type: requiresManualCheck ? 'error' : 'warning',
    };
    ElMessage.error(message);
  };

  const canStartMutation = (serverName) => {
    if (serverName === 'US Server') {
      ElMessage.warning('DLL 双服务器变更统一由 CN 协调，请先切换到 CN Server');
      return false;
    }
    if (operationBusy.value) {
      ElMessage.warning('已有 DLL 操作正在执行，请等待完成后重试');
      return false;
    }
    return true;
  };

  const loadInfo = async () => {
    const requestId = ++infoRequestId;
    const serverName = getServerName();
    loading.value = true;
    try {
      const response = await mazdaPost('pluginInfo');
      const data = response?.data || {};
      if (requestId !== infoRequestId || serverName !== getServerName()) return;
      if (data.success === false) {
        ElMessage.error(data.message || '读取插件信息失败');
        return;
      }
      applyInfo(data);
    } catch (error) {
      if (requestId === infoRequestId && serverName === getServerName()) {
        ElMessage.error(errorMessage('读取插件信息失败', error));
      }
    } finally {
      if (requestId === infoRequestId) loading.value = false;
    }
  };

  const chooseFile = (file) => {
    selectedFile.value = file || null;
    return false;
  };

  const uploadDll = async () => {
    if (!selectedFile.value) {
      ElMessage.warning('请先选择 MazdaServer.dll');
      return;
    }
    const name = String(selectedFile.value.name || '');
    if (!name.toLowerCase().endsWith('.dll')) {
      ElMessage.error('只能上传 .dll 文件');
      return;
    }
    if (Number(selectedFile.value.size || 0) > 180 * 1024 * 1024) {
      ElMessage.error('DLL 不能超过 180 MB');
      return;
    }
    const serverName = getServerName();
    if (!canStartMutation(serverName)) return;

    try {
      await confirmDialog(
        '暂存候选 DLL',
        h('div', null, [
          h('p', { class: 'dll-confirm-text' }, [
            `上传会先在${deploymentTarget()}写入不可变版本文件、校验 ABI 并执行自检，然后仅进入候选灰度；不会立即替换当前活动版本。`,
          ]),
          localOnly.value
            ? h('p', { class: 'dll-confirm-text dll-confirm-warn' },
              '当前已开启「不同步对端」，本次上传只处理本端服务器。')
            : null,
          h('p', { class: 'dll-confirm-text' }, '确认继续？'),
          selectedFile.value?.name
            ? artifactBlock(selectedFile.value.name)
            : null,
        ]),
      );
    } catch (_) {
      return;
    }

    if (serverName !== getServerName()) {
      ElMessage.warning('服务器选择已变化，本次上传已取消，请重新确认');
      return;
    }
    if (!canStartMutation(serverName)) return;

    const form = new FormData();
    form.append('file', selectedFile.value);
    if (localOnly.value) form.append('localOnly', '1');
    uploading.value = true;
    try {
      const response = await mazdaPost('pluginUpload', form, {
        timeout: 180000,
        // multipart 场景后端从头读取"不同步对端"开关（表单字段只在解析后可见）
        ...(localOnly.value ? { headers: { 'X-Local-Only': '1' } } : {}),
      });
      const data = response?.data || {};
      if (!data.success) {
        operationFailure('候选 DLL 暂存失败', data);
        return;
      }
      if (serverName === getServerName()) applyInfo(data);
      selectedFile.value = null;
      clearOperationNotice();
      ElMessage.success(data.message || '候选 DLL 已进入灰度');
    } catch (error) {
      operationFailure('候选 DLL 暂存失败', error);
    } finally {
      uploading.value = false;
    }
  };

  const promoteDll = async () => {
    if (!hasCandidate.value) {
      ElMessage.warning('当前没有可提升的候选 DLL');
      return;
    }
    const serverName = getServerName();
    const candidateTransaction = info.value.candidateTransaction;
    if (!canStartMutation(serverName)) return;
    try {
      await confirmDialog(
        '正式提升候选 DLL',
        h('div', null, [
          h('p', { class: 'dll-confirm-text' }, `确认把候选版本正式提升为${deploymentTarget()}活动版本？`),
          artifactBlock(info.value.candidateArtifact),
          h('p', { class: 'dll-confirm-text dll-confirm-sub' }, '提交失败时后端会自动补偿回滚。'),
        ]),
      );
    } catch (_) {
      return;
    }

    if (serverName !== getServerName()) {
      ElMessage.warning('服务器选择已变化，本次提升已取消，请重新确认');
      return;
    }
    if (!canStartMutation(serverName)) return;

    promoting.value = true;
    try {
      const response = await mazdaPost('pluginPromote', {
        transactionId: candidateTransaction,
        ...(localOnly.value ? { localOnly: true } : {}),
      }, { timeout: 180000 });
      const data = response?.data || {};
      if (!data.success) {
        operationFailure('正式提升失败', data);
        await loadInfo();
        return;
      }
      if (serverName === getServerName()) applyInfo(data);
      clearOperationNotice();
      ElMessage.success(data.message || '候选 DLL 已正式提升');
    } catch (error) {
      operationFailure('正式提升失败', error);
      await loadInfo();
    } finally {
      promoting.value = false;
    }
  };

  const abortCandidate = async () => {
    if (!hasCandidate.value) {
      ElMessage.warning('当前没有可撤销的候选 DLL');
      return;
    }
    const serverName = getServerName();
    const candidateTransaction = info.value.candidateTransaction;
    if (!canStartMutation(serverName)) return;
    try {
      await confirmDialog(
        '撤销候选 DLL',
        h('div', null, [
          h('p', { class: 'dll-confirm-text' }, `确认撤销${deploymentTarget()}候选 DLL？当前活动版本不会改变。`),
          info.value.candidateArtifact
            ? artifactBlock(info.value.candidateArtifact)
            : null,
        ]),
      );
    } catch (_) {
      return;
    }

    if (serverName !== getServerName()) {
      ElMessage.warning('服务器选择已变化，本次撤销已取消，请重新确认');
      return;
    }
    if (!canStartMutation(serverName)) return;

    aborting.value = true;
    try {
      const response = await mazdaPost('pluginAbort', {
        transactionId: candidateTransaction,
        ...(localOnly.value ? { localOnly: true } : {}),
      }, { timeout: 120000 });
      const data = response?.data || {};
      if (serverName === getServerName()) applyInfo(data);
      if (!data.success) {
        operationFailure('候选撤销未完全成功', data);
        return;
      }
      clearOperationNotice();
      ElMessage.success(data.message || '候选 DLL 已撤销');
    } catch (error) {
      operationFailure('撤销候选 DLL 失败', error);
      await loadInfo();
    } finally {
      aborting.value = false;
    }
  };

  const reloadDll = async () => {
    const serverName = getServerName();
    if (!canStartMutation(serverName)) return;
    reloading.value = true;
    try {
      const response = await mazdaPost('pluginReload',
        localOnly.value ? { localOnly: true } : {},
        { timeout: 120000 });
      const data = response?.data || {};
      if (!data.success) {
        operationFailure('重新加载失败', data);
        return;
      }
      if (serverName === getServerName()) applyInfo(data);
      clearOperationNotice();
      ElMessage.success(data.message || '活动 DLL 已重新加载');
    } catch (error) {
      operationFailure('重新加载失败', error);
    } finally {
      reloading.value = false;
    }
  };

  const rollbackDll = async (history) => {
    if (!history || history === info.value.activeArtifact) return;
    const serverName = getServerName();
    if (!canStartMutation(serverName)) return;
    try {
      await confirmDialog(
        '回滚 DLL',
        h('div', null, [
          h('p', { class: 'dll-confirm-text' }, `确认让${deploymentTarget()}事务式回滚到以下历史版本？`),
          artifactBlock(history),
        ]),
      );
    } catch (_) {
      return;
    }

    if (serverName !== getServerName()) {
      ElMessage.warning('服务器选择已变化，本次回滚已取消，请重新确认');
      return;
    }
    if (!canStartMutation(serverName)) return;

    rollingBack.value = true;
    try {
      const response = await mazdaPost('pluginRollback', {
        history,
        ...(localOnly.value ? { localOnly: true } : {}),
      }, { timeout: 180000 });
      const data = response?.data || {};
      if (!data.success) {
        operationFailure('回滚失败', data);
        await loadInfo();
        return;
      }
      if (serverName === getServerName()) applyInfo(data);
      clearOperationNotice();
      ElMessage.success(data.message || '已事务式回滚');
    } catch (error) {
      operationFailure('回滚失败', error);
      await loadInfo();
    } finally {
      rollingBack.value = false;
    }
  };

  onMounted(loadInfo);

  return {
    loading,
    uploading,
    promoting,
    aborting,
    reloading,
    rollingBack,
    operationBusy,
    coordinatorAllowed,
    operationNotice,
    selectedFile,
    info,
    historyRows,
    hasCandidate,
    localOnly,
    loadInfo,
    chooseFile,
    uploadDll,
    promoteDll,
    abortCandidate,
    reloadDll,
    rollbackDll,
    clearOperationNotice,
  };
}
